import axios from 'axios'

export enum TrackingEvent {
  PAGE_VIEW = 'page_view',
  COMPARE_ACTION = 'compare_action',
  DESIGN_SWITCH = 'design_switch',
  DIFF_TOGGLE = 'diff_toggle',
  TRIM_SELECT = 'trim_select',
  COLOR_SELECT = 'color_select',
  THEME_CHANGE = 'theme_change',
}

export enum TrackingCategory {
  INTERACTION = 'interaction',
  NAVIGATION = 'navigation',
  PREFERENCE = 'preference',
}

interface TrackingMeta {
  page?: string
  action?: string
  label?: string
  value?: string | number
  timestamp?: number
  sessionId?: string
  userId?: string
  [key: string]: string | number | boolean | undefined
}

interface TrackingEntry {
  category: TrackingCategory
  product: string
  event: TrackingEvent
  meta: TrackingMeta
  timestamp?: number
}

class TrackingService {
  private readonly PRODUCT_ID = 'model_research_page'
  private readonly MONITOR_ENDPOINT =
    import.meta.env.VITE_MONITOR_ENDPOINT ||
    'https://api.monitor.dev.sodasoft.io'
  private readonly STAGE = import.meta.env.VITE_MONITOR_STAGE || 'dev'
  private sessionId: string
  private isEnabled: boolean = true

  constructor() {
    this.sessionId = this.generateSessionId()
    this.isEnabled = import.meta.env.VITE_ENABLE_TRACKING !== 'false'
  }

  private generateSessionId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
  }

  private getStage(): string {
    switch (this.STAGE) {
      case 'dev':
        return 'dev'
      case 'staging':
      case 'stag':
        return 'staging'
      case 'prod':
        return 'prod'
      default:
        return 'test'
    }
  }

  private async sendToMonitor(entry: TrackingEntry): Promise<void> {
    if (!this.isEnabled || !this.MONITOR_ENDPOINT) {
      console.log('[Tracking]', entry)
      return
    }

    try {
      const payload = {
        ...entry,
        stage: this.getStage(),
        timestamp: entry.timestamp || Date.now(),
      }

      await axios.post(`${this.MONITOR_ENDPOINT}/log`, payload, {
        timeout: 2000,
      })
    } catch (error) {
      console.error('[Tracking Error]', error)
    }
  }

  async trackPageView(
    page: string,
    meta?: Partial<TrackingMeta>,
  ): Promise<void> {
    const entry: TrackingEntry = {
      category: TrackingCategory.NAVIGATION,
      product: this.PRODUCT_ID,
      event: TrackingEvent.PAGE_VIEW,
      meta: {
        page,
        sessionId: this.sessionId,
        ...meta,
        timestamp: Date.now(),
      },
    }

    await this.sendToMonitor(entry)
  }

  async trackCompareAction(
    action: 'add' | 'remove' | 'clear' | 'view',
    trimIds: string[],
    meta?: Partial<TrackingMeta>,
  ): Promise<void> {
    const entry: TrackingEntry = {
      category: TrackingCategory.INTERACTION,
      product: this.PRODUCT_ID,
      event: TrackingEvent.COMPARE_ACTION,
      meta: {
        action,
        trimIds: trimIds.join(','),
        trimCount: trimIds.length,
        sessionId: this.sessionId,
        ...meta,
        timestamp: Date.now(),
      },
    }

    await this.sendToMonitor(entry)
  }

  async trackDesignSwitch(
    fromLayout: string,
    toLayout: string,
    meta?: Partial<TrackingMeta>,
  ): Promise<void> {
    const entry: TrackingEntry = {
      category: TrackingCategory.PREFERENCE,
      product: this.PRODUCT_ID,
      event: TrackingEvent.DESIGN_SWITCH,
      meta: {
        fromLayout,
        toLayout,
        sessionId: this.sessionId,
        ...meta,
        timestamp: Date.now(),
      },
    }

    await this.sendToMonitor(entry)
  }

  async trackDiffToggle(
    enabled: boolean,
    meta?: Partial<TrackingMeta>,
  ): Promise<void> {
    const entry: TrackingEntry = {
      category: TrackingCategory.INTERACTION,
      product: this.PRODUCT_ID,
      event: TrackingEvent.DIFF_TOGGLE,
      meta: {
        action: enabled ? 'enable' : 'disable',
        sessionId: this.sessionId,
        ...meta,
        timestamp: Date.now(),
      },
    }

    await this.sendToMonitor(entry)
  }

  async trackTrimSelect(
    trimId: string,
    trimName: string,
    meta?: Partial<TrackingMeta>,
  ): Promise<void> {
    const entry: TrackingEntry = {
      category: TrackingCategory.INTERACTION,
      product: this.PRODUCT_ID,
      event: TrackingEvent.TRIM_SELECT,
      meta: {
        trimId,
        trimName,
        sessionId: this.sessionId,
        ...meta,
        timestamp: Date.now(),
      },
    }

    await this.sendToMonitor(entry)
  }

  async trackThemeChange(
    theme: 'light' | 'dark',
    meta?: Partial<TrackingMeta>,
  ): Promise<void> {
    const entry: TrackingEntry = {
      category: TrackingCategory.PREFERENCE,
      product: this.PRODUCT_ID,
      event: TrackingEvent.THEME_CHANGE,
      meta: {
        theme,
        sessionId: this.sessionId,
        ...meta,
        timestamp: Date.now(),
      },
    }

    await this.sendToMonitor(entry)
  }

  async trackModelComparison(
    action: 'open' | 'compare' | 'close',
    comparedModel?: {
      year: number | string
      make: string
      model: string
    },
    meta?: Partial<TrackingMeta>,
  ): Promise<void> {
    const entry: TrackingEntry = {
      category: TrackingCategory.INTERACTION,
      product: this.PRODUCT_ID,
      event: TrackingEvent.COMPARE_ACTION,
      meta: {
        action,
        comparisonType: 'model',
        comparedModel: comparedModel
          ? `${comparedModel.year} ${comparedModel.make} ${comparedModel.model}`
          : undefined,
        sessionId: this.sessionId,
        ...meta,
        timestamp: Date.now(),
      },
    }

    await this.sendToMonitor(entry)
  }

  async track(
    event: TrackingEvent,
    category: TrackingCategory,
    meta: TrackingMeta,
  ): Promise<void> {
    const entry: TrackingEntry = {
      category,
      product: this.PRODUCT_ID,
      event,
      meta: {
        ...meta,
        sessionId: this.sessionId,
        timestamp: Date.now(),
      },
    }

    await this.sendToMonitor(entry)
  }
}

export const tracker = new TrackingService()
